/**
 * Anleitungs-Entwurf selbst bearbeiten — Titel, Gültigkeit, Kategorie, Tags, Text mit Vorschau.
 * Gespeichert wird als neue Fassung „vom Techniker"; KIAN baut beim nächsten Überarbeiten darauf auf.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Group, Modal, Select, Stack, Tabs, Text, TextInput, Textarea } from '@mantine/core';
import type { KianKnowledgeDraft, KianKnowledgeResolved } from '../../../lib/api/domains/kianActions';
import { KnowledgeBaseMarkdown } from '../../knowledgeBase/KnowledgeBaseMarkdown';
import { KbCustomerScopeField, type KbCustomerScopeValue } from '../../knowledgeBase/KbCustomerScopeField';
import { KB_CATEGORIES, KB_MIN_CONTENT, KB_MIN_TITLE } from '../../knowledgeBase/kbFormat';

export function KianKnowledgeArticleEditorModal({
  opened,
  onClose,
  article,
  customers,
  saving,
  onSave,
}: {
  opened: boolean;
  onClose: () => void;
  article: KianKnowledgeDraft;
  customers: KianKnowledgeResolved['customers'];
  saving: boolean;
  onSave: (draft: Partial<KianKnowledgeDraft>) => void;
}) {
  const [title, setTitle] = useState(article.title);
  const [content, setContent] = useState(article.content);
  const [category, setCategory] = useState(article.category);
  const [tags, setTags] = useState(article.tags.join(', '));
  const [scope, setScope] = useState<KbCustomerScopeValue>({ scope: article.scope, customers });
  const [tab, setTab] = useState<string | null>('text');

  // Beim Öffnen den aktuellen Stand übernehmen — KIAN kann ihn inzwischen überarbeitet haben
  useEffect(() => {
    if (!opened) return;
    setTitle(article.title);
    setContent(article.content);
    setCategory(article.category);
    setTags(article.tags.join(', '));
    setScope({ scope: article.scope, customers: article.scope === 'CUSTOMERS' ? customers : [] });
    setTab('text');
  }, [opened, article, customers]);

  const categories = useMemo(() => [...new Set([...KB_CATEGORIES, article.category].filter(Boolean))], [article.category]);
  const titleShort = title.trim().length < KB_MIN_TITLE;
  const contentShort = content.trim().length < KB_MIN_CONTENT;
  const scopeMissing = scope.scope === 'CUSTOMERS' && scope.customers.length === 0;

  const save = () =>
    onSave({
      title: title.trim(),
      content: content.trim(),
      category: category.trim(),
      tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      scope: scope.scope,
      customerIds: scope.scope === 'CUSTOMERS' ? scope.customers.map((c) => c.id) : [],
      // Die Auswahl hier ersetzt alle noch offenen Nennungen
      customerRefs: [],
    });

  return (
    <Modal opened={opened} onClose={onClose} title="Anleitung bearbeiten" size="xl" centered zIndex={5300}>
      <Stack gap="sm">
        <TextInput
          label="Titel"
          description="Nennt das Problem oder die Aufgabe — daraus entsteht auch die Adresse der Anleitung."
          value={title}
          onChange={(e) => {
            const v = e.currentTarget.value;
            setTitle(v);
          }}
          error={titleShort ? `Mindestens ${KB_MIN_TITLE} Zeichen` : undefined}
        />
        <KbCustomerScopeField value={scope} onChange={setScope} error={scopeMissing ? 'Mindestens einen Kunden wählen' : null} />
        <Group grow align="flex-start">
          <Select
            label="Kategorie"
            data={categories}
            value={category}
            onChange={(v) => setCategory(v ?? category)}
            searchable
            comboboxProps={{ zIndex: 5400 }}
          />
          <TextInput
            label="Tags"
            description="Mit Komma getrennt"
            value={tags}
            onChange={(e) => {
              const v = e.currentTarget.value;
              setTags(v);
            }}
          />
        </Group>
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="text">Text (Markdown)</Tabs.Tab>
            <Tabs.Tab value="preview">Vorschau</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="text" pt="xs">
            <Textarea
              value={content}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setContent(v);
              }}
              autosize
              minRows={12}
              maxRows={28}
              styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontSize: 13 } }}
              error={contentShort ? `Mindestens ${KB_MIN_CONTENT} Zeichen` : undefined}
            />
            <Text size="xs" c="dimmed" mt={4}>
              Befehle als Codeblock mit drei Backticks, z. B. ```powershell. Keine Passwörter oder Schlüssel — Platzhalter wie
              &lt;Benutzername&gt; verwenden.
            </Text>
          </Tabs.Panel>
          <Tabs.Panel value="preview" pt="xs">
            <KnowledgeBaseMarkdown content={content || '_Noch kein Text._'} />
          </Tabs.Panel>
        </Tabs>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Abbrechen
          </Button>
          <Button color="cyberOrange" loading={saving} onClick={save}>
            Speichern
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
